import * as XLSX from "xlsx-js-style";
import JSZip from "jszip";
import { TIME_ZONE, formatMinutes } from "./presence";

/**
 * "Team Attendance Report" workbook (orange theme), same layout as the team's
 * attendance analysis sheet:
 *   Analysis – title band, Settings (yellow cells are editable), At a glance +
 *              a column chart (worked vs weekly target), 1) team day by day,
 *              2) each person (with data bars), 3) hours per person per day,
 *              and consistency checks. Real formulas over the Calc sheet, with
 *              values pre-computed; status / day colours are conditional
 *              formats, so they follow the settings.
 *   attendance-<from> – the bot's export rows (same columns as its CSV).
 *   Calc     – one row per source row, durations in minutes.
 * The chart and conditional formats are added to the file after SheetJS
 * writes it (SheetJS cannot write them).
 */

const C = {
  band: "843C0C", // title band (dark orange-brown)
  bar: "C55A11", // section bars
  head: "A0430A", // table headers
  panel: "FCE4D6", // settings / glance panel
  panelAlt: "FDF1E9",
  input: "FFF2CC",
  inputText: "843C0C",
  total: "F8CBAD",
  line: "E7C3A8",
  grey: "595959",
  white: "FFFFFF",
  // conditional formats
  met: ["C6EFCE", "006100"],
  close: ["FFEB9C", "7F6000"],
  below: ["FFC7CE", "9C0006"],
  dataBar: "F4B183",
  chartBar: "E68005",
  chartLine: "C00000",
};

const thin = { style: "thin", color: { rgb: C.line } };
const border = { top: thin, bottom: thin, left: thin, right: thin };
const F = (o = {}) => ({ name: "Calibri", sz: 11, ...o });
const fill = (rgb) => ({ patternType: "solid", fgColor: { rgb } });
const S = {
  band: { font: F({ sz: 18, bold: true, color: { rgb: C.white } }), fill: fill(C.band), alignment: { vertical: "center" } },
  bandSub: { font: F({ sz: 10, italic: true, color: { rgb: "F8CBAD" } }), fill: fill(C.band), alignment: { vertical: "top" } },
  bar: { font: F({ sz: 12, bold: true, color: { rgb: C.white } }), fill: fill(C.bar), alignment: { vertical: "center" } },
  panelLabel: { font: F(), fill: fill(C.panel) },
  panelNote: { font: F({ sz: 9, italic: true, color: { rgb: C.grey } }), fill: fill(C.panel), alignment: { wrapText: true, vertical: "center" } },
  input: { font: F({ bold: true, color: { rgb: C.inputText } }), fill: fill(C.input), alignment: { horizontal: "center" }, border },
  glanceLabel: (alt) => ({ font: F({ bold: true }), fill: fill(alt ? C.panelAlt : C.panel), alignment: { vertical: "center" } }),
  glanceValue: (alt) => ({ font: F({ bold: true, sz: 12, color: { rgb: C.band } }), fill: fill(alt ? C.panelAlt : C.panel), alignment: { horizontal: "center", vertical: "center" } }),
  glanceNote: (alt) => ({ font: F({ sz: 10, color: { rgb: C.grey } }), fill: fill(alt ? C.panelAlt : C.panel), alignment: { wrapText: true, vertical: "center" } }),
  head: { font: F({ sz: 10, bold: true, color: { rgb: C.white } }), fill: fill(C.head), alignment: { horizontal: "center", vertical: "center", wrapText: true }, border },
  cell: { font: F(), border, alignment: { vertical: "center" } },
  center: { font: F(), border, alignment: { horizontal: "center", vertical: "center" } },
  num: { font: F(), border, alignment: { horizontal: "center" } },
  total: { font: F({ bold: true }), fill: fill(C.total), border, alignment: { horizontal: "center" } },
  totalLabel: { font: F({ bold: true }), fill: fill(C.total), border },
  note: { font: F({ sz: 9, italic: true, color: { rgb: C.grey } }), alignment: { wrapText: true, vertical: "top" } },
  check: { font: F({ bold: true, color: { rgb: "006100" } }) },
};
const HOURS = '0.0;[Red]\\(0.0\\);"-"';
const STATUS_F = (cell) => `IF(${cell}>=1,"✔ Target met",IF(${cell}>=$B$7,"▲ Close","✖ Below target"))`;
const statusText = (pct, closeAt) => (pct >= 1 ? "✔ Target met" : pct >= closeAt ? "▲ Close" : "✖ Below target");

const dateTime = (d) =>
  d
    ? new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
        .format(new Date(d))
        .replace(",", "")
    : "";
const weekday = (date) => new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });
const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Build the workbook (SheetJS) and return the cell ranges the chart / formats need. */
const buildWorkbook = (rows, from, to, dailyHours) => {
  const closeAt = 0.8;
  const dates = [...new Set(rows.map((r) => r.date))].sort();
  const workingDays = dates.length || 1;
  const weekly = dailyHours * workingDays;
  const rawName = `attendance-${from}`.slice(0, 31);
  const q = `'${rawName}'`;
  const last = 2 + rows.length;
  const enc = (r, c) => XLSX.utils.encode_cell({ r: r - 1, c });
  const col = (c) => XLSX.utils.encode_col(c);

  const put = (ws, addr, v, s, extra = {}) => {
    ws[addr] = { v, t: typeof v === "number" ? "n" : "s", s, ...extra };
  };
  const fput = (ws, addr, f, v, s, z) => {
    ws[addr] = { f, v, t: typeof v === "number" ? "n" : "s", s, ...(z ? { z } : {}) };
  };
  /** Fill a whole row span with a style (for bars / panels). */
  const band = (ws, r, c0, c1, s) => {
    for (let c = c0; c <= c1; c++) if (!ws[enc(r, c)]) put(ws, enc(r, c), "", s);
  };

  // ---------------- raw + Calc
  const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date) || new Date(a.firstCheckIn) - new Date(b.firstCheckIn));
  const raw = {};
  put(raw, "A1", `attendance-${from}_${to}`, { font: F({ bold: true, sz: 13, color: { rgb: C.band } }) });
  ["Date", "User ID", "Username", "Display Name", "Sessions", `First Check In (${TIME_ZONE})`, `Last Check Out (${TIME_ZONE})`, "Total Time", "Idle Count", "Idle Time", "Worked Minutes", "Worked Time", "Status"].forEach((h, i) =>
    put(raw, enc(2, i), h, S.head),
  );
  sorted.forEach((r, i) =>
    [r.date, r.userId, r.username, r.displayName, r.sessions, dateTime(r.firstCheckIn), dateTime(r.lastCheckOut), formatMinutes(r.totalMinutes), r.idleCount, formatMinutes(r.idleMinutes), r.workedMinutes, formatMinutes(r.workedMinutes), r.status].forEach((v, c) =>
      put(raw, enc(3 + i, c), v, S.cell),
    ),
  );
  raw["!ref"] = `A1:M${Math.max(last, 2)}`;
  raw["!cols"] = [11, 20, 20, 20, 9, 20, 20, 11, 10, 10, 14, 12, 10].map((wch) => ({ wch }));

  const calc = {};
  put(calc, "A1", "Helper sheet: the attendance durations in minutes (one row per source row)", S.note);
  ["Date", "Username", "Total Min", "Idle Min", "Worked Min", "Idle Count", "Sessions"].forEach((h, i) => put(calc, enc(2, i), h, S.head));
  sorted.forEach((r, i) => {
    const row = 3 + i;
    fput(calc, `A${row}`, `${q}!A${row}`, r.date, S.cell);
    fput(calc, `B${row}`, `${q}!C${row}`, r.username, S.cell);
    put(calc, `C${row}`, r.totalMinutes, S.num);
    put(calc, `D${row}`, r.idleMinutes, S.num);
    fput(calc, `E${row}`, `${q}!K${row}`, r.workedMinutes, S.num);
    fput(calc, `F${row}`, `${q}!I${row}`, r.idleCount, S.num);
    fput(calc, `G${row}`, `${q}!E${row}`, r.sessions, S.num);
  });
  calc["!ref"] = `A1:G${Math.max(last, 2)}`;
  calc["!cols"] = [12, 22, 10, 10, 11, 10, 9].map((wch) => ({ wch }));
  const rng = (c) => `Calc!$${c}$3:$${c}$${last}`;

  // ---------------- aggregates (hours)
  const people = new Map();
  for (const r of sorted) {
    const p = people.get(r.username) || { username: r.username, name: r.displayName, days: new Map(), sessions: 0, total: 0, idle: 0, worked: 0, idleCount: 0 };
    p.name = r.displayName;
    p.sessions += r.sessions;
    p.total += r.totalMinutes / 60;
    p.idle += r.idleMinutes / 60;
    p.worked += r.workedMinutes / 60;
    p.idleCount += r.idleCount;
    p.days.set(r.date, (p.days.get(r.date) || 0) + r.workedMinutes / 60);
    people.set(r.username, p);
  }
  const P = [...people.values()].sort((a, b) => b.worked - a.worked);
  const byDay = dates.map((d) => {
    const list = sorted.filter((r) => r.date === d);
    const sum = (k) => list.reduce((s, r) => s + r[k], 0);
    return { date: d, people: list.length, sessions: sum("sessions"), total: sum("totalMinutes") / 60, idle: sum("idleMinutes") / 60, worked: sum("workedMinutes") / 60 };
  });
  const tWorked = byDay.reduce((s, d) => s + d.worked, 0);
  const tTotal = byDay.reduce((s, d) => s + d.total, 0);
  const tIdle = byDay.reduce((s, d) => s + d.idle, 0);
  const tSessions = byDay.reduce((s, d) => s + d.sessions, 0);
  const attDays = byDay.reduce((s, d) => s + d.people, 0);
  const weeklyAll = weekly * P.length;
  const atTargetOf = (p) => [...p.days.values()].filter((h) => h >= dailyHours).length;
  const daysAtTarget = P.reduce((s, p) => s + atTargetOf(p), 0);
  const best = byDay.reduce((b, d) => (!b || d.worked > b.worked ? d : b), null);
  const slow = byDay.reduce((b, d) => (!b || d.worked < b.worked ? d : b), null);

  // ---------------- layout
  const a = {};
  const s1 = 22;
  const dh = s1 + 1;
  const d0 = dh + 1;
  const d1 = d0 + byDay.length - 1;
  const dT = d1 + 1;
  const s2 = dT + 3;
  const ph = s2 + 1;
  const p0 = ph + 1;
  const p1 = p0 + P.length - 1;
  const pT = p1 + 1;
  const s3 = pT + 3;
  const xh = s3 + 1;
  const x0 = xh + 1;
  const x1 = x0 + P.length - 1;
  const xT = x1 + 1;
  const chk = xT + 3;
  const lastDayCol = col(dates.length);
  const cT = col(1 + dates.length);
  const cG = col(2 + dates.length);
  const cD = col(3 + dates.length);

  // Title band
  put(a, "A1", "Team Attendance Report", S.band);
  put(a, "A2", `Period ${from} to ${to}  •  All time figures are in hours (h)  •  Yellow cells are settings you can change`, S.bandSub);
  band(a, 1, 1, 13, S.band);
  band(a, 2, 1, 13, S.bandSub);

  // Settings
  put(a, "A4", "Settings", S.bar);
  band(a, 4, 1, 5, S.bar);
  const settings = [
    ["Daily working hours (h)", null, dailyHours, "0.0", "working hours expected per person per day"],
    ["Weekly working hours (h)", "B5*B8", weekly, "0", "weekly target = daily hours × working days"],
    ["“Close to target” starts at", null, closeAt, "0%", "share of target at which status shows “Close”"],
    ["Working days in this period", null, workingDays, "0", `${workingDays} day${workingDays === 1 ? "" : "s"} with attendance — change it for holidays`],
  ];
  settings.forEach(([label, f, v, z, note], i) => {
    const r = 5 + i;
    put(a, `A${r}`, label, S.panelLabel);
    if (f) fput(a, `B${r}`, f, v, S.input, z);
    else put(a, `B${r}`, v, S.input, { z });
    put(a, `C${r}`, note, S.panelNote);
    band(a, r, 3, 5, S.panelNote);
  });

  // At a glance
  put(a, "A9", "At a glance", S.bar);
  band(a, 9, 1, 5, S.bar);
  const glance = [
    ["Hours worked by the team", `G${pT}`, tWorked, '0.0" h"', `"of "&TEXT(J${pT},"0")&" h target for "&COUNTA(B${p0}:B${p1})&" people"`, `of ${Math.round(weeklyAll)} h target for ${P.length} people`],
    ["Goal reached", `L${pT}`, weeklyAll ? tWorked / weeklyAll : 0, "0%", `"team total vs "&TEXT(J${pT},"0")&" h"`, `team total vs ${Math.round(weeklyAll)} h`],
    ["People who reached the target", `COUNTIF(L${p0}:L${p1},">=1")`, P.filter((p) => p.worked >= weekly).length, "0", `"out of "&COUNTA(B${p0}:B${p1})&" people"`, `out of ${P.length} people`],
    ["Days that reached the daily target", `M${pT}`, daysAtTarget, "0", `"out of "&C${dT}&" attendance days ("&TEXT(M${pT}/C${dT},"0%")&")"`, `out of ${attDays} attendance days (${attDays ? Math.round((daysAtTarget / attDays) * 100) : 0}%)`],
    ["Avg worked per person per day", `H${dT}`, attDays ? tWorked / attDays : 0, '0.0" h"', `"vs "&TEXT(B5,"0")&" h daily target"`, `vs ${dailyHours} h daily target`],
    ["Time lost to idle", `IFERROR(F${dT}/E${dT},0)`, tTotal ? tIdle / tTotal : 0, "0.0%", `TEXT(F${dT},"0.0")&" h idle out of "&TEXT(E${dT},"0.0")&" h logged"`, `${tIdle.toFixed(1)} h idle out of ${tTotal.toFixed(1)} h logged`],
    ["Best day", `INDEX(A${d0}:A${d1},MATCH(MAX(G${d0}:G${d1}),G${d0}:G${d1},0))`, best?.date || "", null, `TEXT(MAX(G${d0}:G${d1}),"0.0")&" h worked ("&INDEX(B${d0}:B${d1},MATCH(MAX(G${d0}:G${d1}),G${d0}:G${d1},0))&")"`, best ? `${best.worked.toFixed(1)} h worked (${weekday(best.date)})` : ""],
    ["Slowest day", `INDEX(A${d0}:A${d1},MATCH(MIN(G${d0}:G${d1}),G${d0}:G${d1},0))`, slow?.date || "", null, `TEXT(MIN(G${d0}:G${d1}),"0.0")&" h worked ("&INDEX(B${d0}:B${d1},MATCH(MIN(G${d0}:G${d1}),G${d0}:G${d1},0))&")"`, slow ? `${slow.worked.toFixed(1)} h worked (${weekday(slow.date)})` : ""],
    ["Most hours worked", `INDEX(A${p0}:A${p1},MATCH(MAX(G${p0}:G${p1}),G${p0}:G${p1},0))`, P[0]?.name || "", null, `TEXT(MAX(G${p0}:G${p1}),"0.0")&" h worked"`, P[0] ? `${P[0].worked.toFixed(1)} h worked` : ""],
    ["Fewest hours worked", `INDEX(A${p0}:A${p1},MATCH(MIN(G${p0}:G${p1}),G${p0}:G${p1},0))`, P[P.length - 1]?.name || "", null, `TEXT(MIN(G${p0}:G${p1}),"0.0")&" h worked"`, P.length ? `${P[P.length - 1].worked.toFixed(1)} h worked` : ""],
  ];
  glance.forEach(([label, f, v, z, nf, nv], i) => {
    const r = 10 + i;
    const alt = i % 2 === 1;
    put(a, `A${r}`, label, S.glanceLabel(alt));
    fput(a, `B${r}`, f, v, S.glanceValue(alt), z);
    fput(a, `C${r}`, nf, nv, S.glanceNote(alt));
    band(a, r, 3, 5, S.glanceNote(alt));
  });

  // Chart frame (the chart itself is added to the file later)
  put(a, "H9", "Worked hours vs weekly target", S.bar);
  band(a, 9, 8, 13, S.bar);

  // 1. Team – day by day
  put(a, `A${s1}`, "1.  Team – day by day", S.bar);
  band(a, s1, 1, 11, S.bar);
  ["Date", "Day", "People Present", "Sessions", "Total Time (h)", "Idle Time (h)", "Worked (h)", "Avg Worked per Person (h)", "Target (h)", "Gap vs Target (h)", "Achieved", "Status"].forEach((h, i) => put(a, enc(dh, i), h, S.head));
  byDay.forEach((d, i) => {
    const r = d0 + i;
    const target = d.people * dailyHours;
    const pct = target ? d.worked / target : 0;
    put(a, `A${r}`, d.date, S.cell, { z: "@" });
    fput(a, `B${r}`, `TEXT(DATEVALUE(A${r}),"dddd")`, weekday(d.date), S.center);
    fput(a, `C${r}`, `COUNTIFS(${rng("A")},A${r})`, d.people, S.num, "0");
    fput(a, `D${r}`, `SUMIFS(${rng("G")},${rng("A")},A${r})`, d.sessions, S.num, "0");
    fput(a, `E${r}`, `SUMIFS(${rng("C")},${rng("A")},A${r})/60`, d.total, S.num, HOURS);
    fput(a, `F${r}`, `SUMIFS(${rng("D")},${rng("A")},A${r})/60`, d.idle, S.num, HOURS);
    fput(a, `G${r}`, `SUMIFS(${rng("E")},${rng("A")},A${r})/60`, d.worked, S.num, HOURS);
    fput(a, `H${r}`, `IFERROR(G${r}/C${r},0)`, d.people ? d.worked / d.people : 0, S.num, HOURS);
    fput(a, `I${r}`, `C${r}*$B$5`, target, S.num, HOURS);
    fput(a, `J${r}`, `G${r}-I${r}`, d.worked - target, S.num, HOURS);
    fput(a, `K${r}`, `IFERROR(G${r}/I${r},0)`, pct, S.num, "0%");
    fput(a, `L${r}`, STATUS_F(`K${r}`), statusText(pct, closeAt), S.center);
  });
  {
    const target = attDays * dailyHours;
    const pct = target ? tWorked / target : 0;
    put(a, `A${dT}`, "Team total", S.totalLabel);
    put(a, `B${dT}`, "", S.total);
    [["C", `SUM(C${d0}:C${d1})`, attDays, "0"], ["D", `SUM(D${d0}:D${d1})`, tSessions, "0"], ["E", `SUM(E${d0}:E${d1})`, tTotal, HOURS], ["F", `SUM(F${d0}:F${d1})`, tIdle, HOURS], ["G", `SUM(G${d0}:G${d1})`, tWorked, HOURS], ["H", `IFERROR(G${dT}/C${dT},0)`, attDays ? tWorked / attDays : 0, HOURS], ["I", `SUM(I${d0}:I${d1})`, target, HOURS], ["J", `G${dT}-I${dT}`, tWorked - target, HOURS], ["K", `IFERROR(G${dT}/I${dT},0)`, pct, "0%"]].forEach(([c, f, v, z]) =>
      fput(a, `${c}${dT}`, f, v, S.total, z),
    );
    fput(a, `L${dT}`, STATUS_F(`K${dT}`), statusText(pct, closeAt), S.total);
  }
  put(a, `A${dT + 1}`, "People Present = people who checked in that day (total = attendance days). Target = people present × daily target.", S.note);

  // 2. Each person
  put(a, `A${s2}`, "2.  Each person – the period", S.bar);
  band(a, s2, 1, 13, S.bar);
  ["Name", "Username", "Days Attended", "Sessions", "Total Time (h)", "Idle Time (h)", "Worked (h)", "Idle Events", "Avg Worked per Day (h)", "Target (h)", "Gap vs Target (h)", "Achieved", "Days at Daily Target", "Status"].forEach((h, i) =>
    put(a, enc(ph, i), h, S.head),
  );
  P.forEach((p, i) => {
    const r = p0 + i;
    const days = p.days.size;
    const pct = weekly ? p.worked / weekly : 0;
    put(a, `A${r}`, p.name, S.cell);
    put(a, `B${r}`, p.username, S.cell);
    fput(a, `C${r}`, `COUNTIFS(${rng("B")},B${r})`, days, S.num, "0");
    fput(a, `D${r}`, `SUMIFS(${rng("G")},${rng("B")},B${r})`, p.sessions, S.num, "0");
    fput(a, `E${r}`, `SUMIFS(${rng("C")},${rng("B")},B${r})/60`, p.total, S.num, HOURS);
    fput(a, `F${r}`, `SUMIFS(${rng("D")},${rng("B")},B${r})/60`, p.idle, S.num, HOURS);
    fput(a, `G${r}`, `SUMIFS(${rng("E")},${rng("B")},B${r})/60`, p.worked, S.num, HOURS);
    fput(a, `H${r}`, `SUMIFS(${rng("F")},${rng("B")},B${r})`, p.idleCount, S.num, "0");
    fput(a, `I${r}`, `IFERROR(G${r}/C${r},0)`, days ? p.worked / days : 0, S.num, HOURS);
    fput(a, `J${r}`, "$B$6", weekly, S.num, "0.0");
    fput(a, `K${r}`, `G${r}-J${r}`, p.worked - weekly, S.num, HOURS);
    fput(a, `L${r}`, `IFERROR(G${r}/J${r},0)`, pct, S.num, "0%");
    fput(a, `M${r}`, `${cD}${x0 + i}`, atTargetOf(p), S.num, "0");
    fput(a, `N${r}`, STATUS_F(`L${r}`), statusText(pct, closeAt), S.center);
  });
  {
    const pct = weeklyAll ? tWorked / weeklyAll : 0;
    const sum = (k) => P.reduce((s, p) => s + p[k], 0);
    put(a, `A${pT}`, "Team total", S.totalLabel);
    put(a, `B${pT}`, "", S.total);
    [["C", attDays, "0"], ["D", sum("sessions"), "0"], ["E", sum("total"), HOURS], ["F", sum("idle"), HOURS], ["G", tWorked, HOURS], ["H", sum("idleCount"), "0"]].forEach(([c, v, z]) =>
      fput(a, `${c}${pT}`, `SUM(${c}${p0}:${c}${p1})`, v, S.total, z),
    );
    fput(a, `I${pT}`, `IFERROR(G${pT}/C${pT},0)`, attDays ? tWorked / attDays : 0, S.total, HOURS);
    fput(a, `J${pT}`, `SUM(J${p0}:J${p1})`, weeklyAll, S.total, "0.0");
    fput(a, `K${pT}`, `G${pT}-J${pT}`, tWorked - weeklyAll, S.total, HOURS);
    fput(a, `L${pT}`, `IFERROR(G${pT}/J${pT},0)`, pct, S.total, "0%");
    fput(a, `M${pT}`, `SUM(M${p0}:M${p1})`, daysAtTarget, S.total, "0");
    fput(a, `N${pT}`, STATUS_F(`L${pT}`), statusText(pct, closeAt), S.total);
  }
  put(a, `A${pT + 1}`, "People are listed from most to least hours worked. Idle Events = number of idle alerts. Status: ✔ met target • ▲ close • ✖ below.", S.note);

  // 3. Person by day
  put(a, `A${s3}`, "3.  Hours worked – person by day", S.bar);
  band(a, s3, 1, 3 + dates.length, S.bar);
  put(a, `A${xh}`, "Name", S.head);
  dates.forEach((d, i) => fput(a, enc(xh, 1 + i), `A${d0 + i}`, d, S.head));
  put(a, `${cT}${xh}`, "Total (h)", S.head);
  put(a, `${cG}${xh}`, "Gap vs Target (h)", S.head);
  put(a, `${cD}${xh}`, "Days at Daily Target", S.head);
  P.forEach((p, i) => {
    const r = x0 + i;
    fput(a, `A${r}`, `A${p0 + i}`, p.name, S.cell);
    dates.forEach((d, di) => {
      const c = col(1 + di);
      fput(a, `${c}${r}`, `SUMIFS(${rng("E")},${rng("B")},$B${p0 + i},${rng("A")},${c}$${xh})/60`, p.days.get(d) || 0, S.num, HOURS);
    });
    fput(a, `${cT}${r}`, `SUM(B${r}:${lastDayCol}${r})`, p.worked, S.num, HOURS);
    fput(a, `${cG}${r}`, `${cT}${r}-$B$6`, p.worked - weekly, S.num, HOURS);
    fput(a, `${cD}${r}`, `COUNTIF(B${r}:${lastDayCol}${r},">="&$B$5)`, atTargetOf(p), S.num, "0");
  });
  put(a, `A${xT}`, "Team total", S.totalLabel);
  dates.forEach((d, di) => fput(a, `${col(1 + di)}${xT}`, `SUM(${col(1 + di)}${x0}:${col(1 + di)}${x1})`, byDay[di].worked, S.total, HOURS));
  fput(a, `${cT}${xT}`, `SUM(${cT}${x0}:${cT}${x1})`, tWorked, S.total, HOURS);
  fput(a, `${cG}${xT}`, `SUM(${cG}${x0}:${cG}${x1})`, tWorked - weeklyAll, S.total, HOURS);
  fput(a, `${cD}${xT}`, `SUM(${cD}${x0}:${cD}${x1})`, daysAtTarget, S.total, "0");
  put(a, `A${xT + 1}`, "Green = reached the daily target • Red = worked but below the daily target • “-” = no hours worked that day.", S.note);

  // Checks
  put(a, `A${chk}`, "Check: sessions match the source sheet", { font: F({ sz: 10 }) });
  fput(a, `B${chk}`, `IF(AND(D${dT}=SUM(${q}!E3:E${last}),D${pT}=D${dT}),"OK","MISMATCH")`, "OK", S.check);
  put(a, `A${chk + 1}`, "Check: worked hours match the source sheet", { font: F({ sz: 10 }) });
  fput(a, `B${chk + 1}`, `IF(AND(ABS(G${dT}-SUM(${q}!K3:K${last})/60)<0.001,ABS(G${pT}-G${dT})<0.001,ABS(${cT}${xT}-G${dT})<0.001),"OK","MISMATCH")`, "OK", S.check);

  a["!ref"] = `A1:N${chk + 1}`;
  a["!cols"] = [32, 20, 14, 12, 13, 13, 13, 16, 13, 14, 15, 14, 14, 16].map((wch) => ({ wch }));
  a["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 13 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 13 } },
    ...[5, 6, 7, 8].map((r) => ({ s: { r: r - 1, c: 2 }, e: { r: r - 1, c: 5 } })),
    ...glance.map((_, i) => ({ s: { r: 9 + i, c: 2 }, e: { r: 9 + i, c: 5 } })),
    { s: { r: dT, c: 0 }, e: { r: dT, c: 7 } },
    { s: { r: pT, c: 0 }, e: { r: pT, c: 7 } },
    { s: { r: xT, c: 0 }, e: { r: xT, c: 7 } },
  ];
  const rowsH = [];
  rowsH[0] = { hpt: 34 };
  rowsH[1] = { hpt: 18 };
  rowsH[2] = { hpt: 8 };
  for (let r = 10; r <= 19; r++) rowsH[r - 1] = { hpt: 26 };
  rowsH[dh - 1] = { hpt: 32 };
  rowsH[ph - 1] = { hpt: 32 };
  rowsH[xh - 1] = { hpt: 32 };
  a["!rows"] = rowsH;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, a, "Analysis");
  XLSX.utils.book_append_sheet(wb, raw, rawName);
  XLSX.utils.book_append_sheet(wb, calc, "Calc");
  return { wb, layout: { d0, dT, p0, p1, pT, x0, x1, lastDayCol, peopleCount: P.length } };
};

// ---------------- post-processing: conditional formats + chart
const DXFS = [C.met, C.close, C.below]
  .map(([bg, fg]) => `<dxf><font><color rgb="FF${fg}"/></font><fill><patternFill patternType="solid"><bgColor rgb="FF${bg}"/></patternFill></fill></dxf>`)
  .join("");

const conditionalFormats = ({ d0, dT, p0, p1, pT, x0, x1, lastDayCol }) => {
  const statusRule = (first, p) =>
    `<cfRule type="containsText" dxfId="0" priority="${p}" operator="containsText" text="met"><formula>NOT(ISERROR(SEARCH("met",${first})))</formula></cfRule>` +
    `<cfRule type="containsText" dxfId="1" priority="${p + 1}" operator="containsText" text="Close"><formula>NOT(ISERROR(SEARCH("Close",${first})))</formula></cfRule>` +
    `<cfRule type="containsText" dxfId="2" priority="${p + 2}" operator="containsText" text="Below"><formula>NOT(ISERROR(SEARCH("Below",${first})))</formula></cfRule>`;
  return (
    `<conditionalFormatting sqref="L${d0}:L${dT}">${statusRule(`L${d0}`, 1)}</conditionalFormatting>` +
    `<conditionalFormatting sqref="N${p0}:N${pT}">${statusRule(`N${p0}`, 4)}</conditionalFormatting>` +
    `<conditionalFormatting sqref="B${x0}:${lastDayCol}${x1}">` +
    `<cfRule type="expression" dxfId="0" priority="7"><formula>B${x0}&gt;=$B$5</formula></cfRule>` +
    `<cfRule type="expression" dxfId="2" priority="8"><formula>AND(B${x0}&gt;0,B${x0}&lt;$B$5)</formula></cfRule>` +
    `</conditionalFormatting>` +
    `<conditionalFormatting sqref="G${p0}:G${p1}"><cfRule type="dataBar" priority="9"><dataBar><cfvo type="min"/><cfvo type="max"/><color rgb="FF${C.dataBar}"/></dataBar></cfRule></conditionalFormatting>`
  );
};

const chartXml = ({ p0, p1 }) => {
  const cat = `Analysis!$A$${p0}:$A$${p1}`;
  const ser = (idx, name, ref, shape) => `
      <c:ser><c:idx val="${idx}"/><c:order val="${idx}"/><c:tx><c:v>${esc(name)}</c:v></c:tx>${shape}
        <c:cat><c:strRef><c:f>${cat}</c:f></c:strRef></c:cat>
        <c:val><c:numRef><c:f>${ref}</c:f></c:numRef></c:val>${idx === 1 ? '<c:smooth val="0"/>' : ""}
      </c:ser>`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<c:chartSpace xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <c:roundedCorners val="0"/>
  <c:chart>
    <c:autoTitleDeleted val="1"/>
    <c:plotArea><c:layout/>
      <c:barChart><c:barDir val="col"/><c:grouping val="clustered"/><c:varyColors val="0"/>${ser(
        0,
        "Worked (h)",
        `Analysis!$G$${p0}:$G$${p1}`,
        `<c:spPr><a:solidFill><a:srgbClr val="${C.chartBar}"/></a:solidFill></c:spPr><c:invertIfNegative val="0"/>`,
      )}
        <c:gapWidth val="80"/><c:axId val="500100"/><c:axId val="500200"/>
      </c:barChart>
      <c:lineChart><c:grouping val="standard"/><c:varyColors val="0"/>${ser(
        1,
        "Weekly target (h)",
        `Analysis!$J$${p0}:$J$${p1}`,
        `<c:spPr><a:ln w="28575"><a:solidFill><a:srgbClr val="${C.chartLine}"/></a:solidFill></a:ln></c:spPr><c:marker><c:symbol val="none"/></c:marker>`,
      )}
        <c:marker val="1"/><c:axId val="500100"/><c:axId val="500200"/>
      </c:lineChart>
      <c:catAx><c:axId val="500100"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="0"/><c:axPos val="b"/><c:numFmt formatCode="General" sourceLinked="0"/><c:tickLblPos val="nextTo"/><c:spPr><a:ln><a:solidFill><a:srgbClr val="BFBFBF"/></a:solidFill></a:ln></c:spPr><c:txPr><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="900"/></a:pPr><a:endParaRPr lang="en-US"/></a:p></c:txPr><c:crossAx val="500200"/><c:crosses val="autoZero"/><c:auto val="1"/><c:lblAlgn val="ctr"/><c:lblOffset val="100"/></c:catAx>
      <c:valAx><c:axId val="500200"/><c:scaling><c:orientation val="minMax"/><c:min val="0"/></c:scaling><c:delete val="0"/><c:axPos val="l"/><c:majorGridlines><c:spPr><a:ln><a:solidFill><a:srgbClr val="E7E6E6"/></a:solidFill></a:ln></c:spPr></c:majorGridlines><c:title><c:tx><c:rich><a:bodyPr rot="-5400000"/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="900"/></a:pPr><a:r><a:rPr lang="en-US" sz="900"/><a:t>Hours</a:t></a:r></a:p></c:rich></c:tx><c:overlay val="0"/></c:title><c:numFmt formatCode="0.0" sourceLinked="0"/><c:tickLblPos val="nextTo"/><c:txPr><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="900"/></a:pPr><a:endParaRPr lang="en-US"/></a:p></c:txPr><c:crossAx val="500100"/><c:crosses val="autoZero"/><c:crossBetween val="between"/></c:valAx>
    </c:plotArea>
    <c:legend><c:legendPos val="b"/><c:overlay val="0"/><c:txPr><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="900"/></a:pPr><a:endParaRPr lang="en-US"/></a:p></c:txPr></c:legend>
    <c:plotVisOnly val="1"/><c:dispBlanksAs val="gap"/>
  </c:chart>
  <c:spPr><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill><a:ln><a:solidFill><a:srgbClr val="D9D9D9"/></a:solidFill></a:ln></c:spPr>
</c:chartSpace>`;
};

const DRAWING = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">
  <xdr:twoCellAnchor editAs="oneCell">
    <xdr:from><xdr:col>7</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>9</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>
    <xdr:to><xdr:col>14</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>20</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>
    <xdr:graphicFrame macro="">
      <xdr:nvGraphicFramePr><xdr:cNvPr id="2" name="Worked vs target"/><xdr:cNvGraphicFramePr/></xdr:nvGraphicFramePr>
      <xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm>
      <a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart"><c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="rId1"/></a:graphicData></a:graphic>
    </xdr:graphicFrame>
    <xdr:clientData/>
  </xdr:twoCellAnchor>
</xdr:wsDr>`;

const REL_NS = "http://schemas.openxmlformats.org/package/2006/relationships";
const rels = (items) =>
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="${REL_NS}">${items}</Relationships>`;

/** Add conditional formats, data bars and the chart to the Analysis sheet. */
export const enhanceWorkbook = async (buffer, layout) => {
  const zip = await JSZip.loadAsync(buffer);
  const sheetPath = "xl/worksheets/sheet1.xml"; // Analysis is the first sheet
  let sheet = await zip.file(sheetPath).async("string");

  // Conditional formats go after <mergeCells> (or </sheetData>), per schema order.
  const cf = conditionalFormats(layout);
  sheet = sheet.includes("</mergeCells>") ? sheet.replace("</mergeCells>", `</mergeCells>${cf}`) : sheet.replace("</sheetData>", `</sheetData>${cf}`);

  if (layout.peopleCount > 0) {
    if (!/xmlns:r=/.test(sheet.slice(0, 600))) {
      sheet = sheet.replace("<worksheet ", '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ');
    }
    // <drawing> comes after pageMargins/pageSetup and before legacyDrawing/tableParts.
    const drawingTag = '<drawing r:id="rIdChart1"/>';
    sheet = /<(legacyDrawing|tableParts|extLst)\b/.test(sheet)
      ? sheet.replace(/<(legacyDrawing|tableParts|extLst)\b/, `${drawingTag}<$1`)
      : sheet.replace("</worksheet>", `${drawingTag}</worksheet>`);

    const relsPath = "xl/worksheets/_rels/sheet1.xml.rels";
    const rel = `<Relationship Id="rIdChart1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing1.xml"/>`;
    const existing = zip.file(relsPath) ? await zip.file(relsPath).async("string") : null;
    zip.file(relsPath, existing ? existing.replace("</Relationships>", `${rel}</Relationships>`) : rels(rel));
    zip.file("xl/drawings/drawing1.xml", DRAWING);
    zip.file(
      "xl/drawings/_rels/drawing1.xml.rels",
      rels(`<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="../charts/chart1.xml"/>`),
    );
    zip.file("xl/charts/chart1.xml", chartXml(layout));

    let types = await zip.file("[Content_Types].xml").async("string");
    types = types.replace(
      "</Types>",
      '<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>' +
        '<Override PartName="/xl/charts/chart1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/></Types>',
    );
    zip.file("[Content_Types].xml", types);
  }
  zip.file(sheetPath, sheet);

  // Differential formats used by the conditional formats (green / amber / red).
  let styles = await zip.file("xl/styles.xml").async("string");
  if (/<dxfs[^>]*\/>/.test(styles)) styles = styles.replace(/<dxfs[^>]*\/>/, `<dxfs count="3">${DXFS}</dxfs>`);
  else if (/<dxfs[^>]*>/.test(styles)) styles = styles.replace(/<dxfs[^>]*>([\s\S]*?)<\/dxfs>/, `<dxfs count="3">${DXFS}</dxfs>`);
  else if (styles.includes("<tableStyles")) styles = styles.replace("<tableStyles", `<dxfs count="3">${DXFS}</dxfs><tableStyles`);
  else styles = styles.replace("</styleSheet>", `<dxfs count="3">${DXFS}</dxfs></styleSheet>`);
  zip.file("xl/styles.xml", styles);

  return zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
};

/** Build the report as bytes (used by the download and by tests). */
export const buildAttendanceReport = async (rows, from, to, { dailyHours = 8 } = {}) => {
  const { wb, layout } = buildWorkbook(rows, from, to, dailyHours);
  const buffer = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  return enhanceWorkbook(buffer, layout);
};

export const exportAttendanceReport = async (rows, from, to, options) => {
  const bytes = await buildAttendanceReport(rows, from, to, options);
  const blob = new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `attendance-${from}_${to}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
